import { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { MARKETS, UI, LANG } from "../data/content";
import { api } from "../lib/api";
import { Reveal, HeroBand, SectionHead, ScopeBlock, RelatedLinks, ContactModule, usePageMeta } from "../components/kit";

const fmtReviewed = (d) =>
  new Date(d).toLocaleDateString(LANG === "el" ? "el-GR" : "en-GB", { day: "numeric", month: "long", year: "numeric" });

const RegTableSection = () => {
  const [regimes, setRegimes] = useState([]);
  useEffect(() => { api.get("/regimes").then(({ data }) => setRegimes(data)).catch(() => {}); }, []);
  if (!regimes.length) return null;
  const latest = regimes.map((r) => r.lastReviewed).filter(Boolean).sort().slice(-1)[0];
  return (
    <section className="k-section !py-14 bg-paper-alt" data-testid="reg-table-section">
      <div className="k-container">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <SectionHead eyebrow={UI.common.regStatus} title={UI.common.perimeter} />
          {latest && <span className="topic-tag mb-10" data-testid="reg-as-at-chip">{UI.common.lastReviewed} {fmtReviewed(latest)}</span>}
        </div>
        <Reveal>
          <table className="k-table" data-testid="regulatory-table">
            <thead><tr><th>{UI.common.regime}</th><th>{UI.common.status}</th><th>{UI.common.lastReviewed}</th></tr></thead>
            <tbody>
              {regimes.map((r) => (
                <tr key={r.regime_id} data-testid={`regime-row-${r.regime_id}`}>
                  <td className="font-semibold">{r.name}</td>
                  <td className="text-ink-muted">{r.status}</td>
                  <td className="text-ink-muted whitespace-nowrap" style={{ fontVariantNumeric: "tabular-nums" }}>{r.lastReviewed ? fmtReviewed(r.lastReviewed) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>
      </div>
    </section>
  );
};

const List = ({ items, testId }) => (
  <ul className="list-none p-0 m-0 space-y-3 max-w-3xl" data-testid={testId}>
    {items.map((q, i) => (
      <li key={i} className="flex gap-4 leading-relaxed text-[0.95rem]">
        <span className="text-brand font-semibold shrink-0">—</span>{q}
      </li>
    ))}
  </ul>
);

const GrowthProjects = ({ m }) => {
  const [tab, setTab] = useState("eu");
  const active = m.geoTabs.find((t) => t.id === tab);
  return (
    <>
      <section className="k-section !py-14 bg-paper-alt" data-testid="context-band-section">
        <div className="k-container">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
            <SectionHead eyebrow={UI.common.marketContext} title={m.contextTitle} />
            <span className="topic-tag mb-10" data-testid="as-at-chip">{m.asAt}</span>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {m.contextBand.map(([title, body], i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div className="border-t-2 border-[var(--accent)] pt-5">
                  <h3 className="text-base font-semibold mb-3">{title}</h3>
                  <p className="text-sm text-ink-muted leading-relaxed">{body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <p className="text-[0.95rem] leading-relaxed max-w-4xl">{m.contextNote}</p>
          </Reveal>
        </div>
      </section>

      <section className="k-section !py-14">
        <div className="k-container">
          <SectionHead eyebrow={m.name} title={UI.common.whoWeAdvise} />
          <List items={m.whoWeAdvise} testId="who-we-advise" />
        </div>
      </section>

      <section className="k-section !py-14 bg-paper-alt" data-testid="funded-projects-section">
        <div className="k-container">
          <SectionHead eyebrow={UI.common.fundedProjects} title={UI.common.questions} />
          <List items={m.fundedQuestions} testId="funded-questions" />
          <Reveal className="mt-8 max-w-4xl">
            <p className="text-sm text-ink-muted leading-relaxed"><span className="font-semibold text-ink">{UI.common.howWeAdvise} </span>{m.fundedHow}</p>
          </Reveal>
          <Reveal className="mt-8 max-w-4xl">
            <p className="text-sm leading-relaxed border border-line bg-paper p-6" data-testid="expectations-note">{m.expectationNote}</p>
          </Reveal>
        </div>
      </section>

      <section className="k-section !py-14" data-testid="growth-projects-section">
        <div className="k-container">
          <SectionHead eyebrow={UI.common.growthProjects} title={UI.common.questions} />
          <List items={m.growthQuestions} testId="growth-questions" />
          <Reveal className="mt-8 max-w-4xl">
            <p className="text-sm text-ink-muted leading-relaxed"><span className="font-semibold text-ink">{UI.common.howWeAdvise} </span>{m.growthHow}</p>
          </Reveal>
        </div>
      </section>

      <section className="k-section !py-14 bg-paper-alt" data-testid="geo-tabs-section">
        <div className="k-container">
          <SectionHead eyebrow={UI.common.geography} title={UI.common.whereWeWork} />
          <div className="flex gap-1">
            {m.geoTabs.map((t) => (
              <button key={t.id} className="geo-tab" data-active={tab === t.id} onClick={() => setTab(t.id)} data-testid={`geo-tab-${t.id}`}>{t.label}</button>
            ))}
          </div>
          <div className="border border-line bg-paper p-8" data-testid={`geo-panel-${tab}`}>
            <div className="grid md:grid-cols-2 gap-8">
              {active.body.map(([title, body]) => (
                <div key={title}>
                  <h3 className="text-base font-semibold mb-2">{title}</h3>
                  <p className="text-sm text-ink-muted leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default function MarketPage() {
  const { slug } = useParams();
  const m = MARKETS.find((x) => x.slug === slug);
  usePageMeta(m ? `${m.name} | KairosHK` : "KairosHK", m?.proposition);
  if (!m) return <Navigate to="/markets" replace />;
  const isGrowth = m.slug === "development-and-growth-projects";

  return (
    <div data-testid={`market-page-${m.slug}`}>
      <HeroBand image={m.image} eyebrow={UI.nav.markets} title={m.name} standfirst={m.proposition} compact />

      {m.context && (
        <section className="k-section !pb-10">
          <div className="k-container max-w-4xl !mx-0">
            <Reveal>
              {m.context.map((p, i) => (
                <p key={i} className={`text-lg leading-relaxed mb-5 ${i > 0 ? "text-ink-muted" : ""}`}>{p}</p>
              ))}
            </Reveal>
          </div>
        </section>
      )}

      {isGrowth ? (
        <GrowthProjects m={m} />
      ) : (
        <>
          {m.showRegTable && <RegTableSection />}

          {m.whoWeAdvise && (
            <section className={`k-section !py-14 ${m.showRegTable ? "" : "bg-paper-alt"}`}>
              <div className="k-container">
                <SectionHead eyebrow={m.name} title={UI.common.whoWeAdvise} />
                <List items={m.whoWeAdvise} testId="who-we-advise" />
              </div>
            </section>
          )}

          {m.stages && (
            <section className="k-section !py-14 bg-paper-alt" data-testid="stages-section">
              <div className="k-container">
                <SectionHead eyebrow={m.name} title={UI.common.byStage} />
                <Reveal>
                  <table className="k-table" data-testid="stages-table">
                    <thead><tr><th>{UI.common.stage}</th><th>{UI.common.whatMatters}</th><th>{UI.common.howWeAdvise.replace(":", "")}</th></tr></thead>
                    <tbody>
                      {m.stages.map(([st, w, h]) => (
                        <tr key={st}><td className="font-semibold">{st}</td><td className="text-ink-muted">{w}</td><td className="text-ink-muted">{h}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </Reveal>
              </div>
            </section>
          )}

          {m.questions && (
            <section className={`k-section !py-14 ${m.stages ? "" : "bg-paper-alt"}`} data-testid="questions-section">
              <div className="k-container">
                <SectionHead eyebrow={m.name} title={m.slug === "start-ups" ? UI.common.questionsFounders : UI.common.questions} />
                <List items={m.questions} testId="client-questions" />
                {m.howWeAdvise && (
                  <Reveal className="mt-8 max-w-4xl">
                    <p className="text-sm text-ink-muted leading-relaxed"><span className="font-semibold text-ink">{UI.common.howWeAdvise} </span>{m.howWeAdvise}</p>
                  </Reveal>
                )}
              </div>
            </section>
          )}
        </>
      )}

      <section className="k-section !py-14">
        <div className="k-container">
          <Reveal><ScopeBlock text={m.scope} /></Reveal>
          <Reveal className="mt-12"><RelatedLinks items={m.related} /></Reveal>
        </div>
      </section>

      <section className="k-container pb-20 pt-2">
        <ContactModule />
      </section>
    </div>
  );
}
